import { Form } from 'formik'
import { useTranslation } from 'react-i18next'

import { TextFieldInput } from '@dhl/formik'
import { useUpdateUserType } from '@dhl/mdp-api'
import { Box, Button, Checkbox, Grid } from '@dhl-ui/components'
import type { User } from '~/modules/users/model'
import { useNotificationMessage } from '~/common'

type Props = {
  onCancel: () => void
  user?: User
  disableForm?: boolean
  disableEmailField?: boolean
}

export const UserForm = ({ onCancel, user, disableForm = false, disableEmailField = false }: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: updateUserType } = useUpdateUserType()
  const { showError, showSuccess } = useNotificationMessage()
  const canHasAccountManager = user ? ['accountManager', 'consumer'].includes(user.userType) : false
  const isAccountManager = user?.userType === 'accountManager'

  const toggleUserType = async () => {
    if (user?.id && user?.userType) {
      updateUserType({
        id: user.id,
        userType: user.userType === 'accountManager' ? 'consumer' : 'accountManager',
      })
        .then(() => {
          showSuccess(t('admin.usersPage.userDetails.accountManagerToggleSuccess'))
        })
        .catch((error) => {
          console.error({ error })
          showError(
            t(
              `admin.usersPage.userDetails.${error?.response?.data?.key === 'invalid_email_domain' ? 'accountManagerToggleEmailError' : 'accountManagerToggleError'}`
            )
          )
        })
        .finally(() => {
          onCancel()
        })
    } else {
      console.error(`user ID or user type is missing: ${user?.id}, ${user?.userType}`)
      showError(t('admin.usersPage.userDetails.accountManagerToggleError'))
    }
  }

  return (
    <Form data-testid="user-form">
      <Box pt={2} pb={4}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              name="name.firstName"
              fullWidth
              label={t('admin.usersPage.userDetails.firstName')}
              required
              disabled={disableForm}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              fullWidth
              label={t('admin.usersPage.userDetails.lastName')}
              name="name.lastName"
              type="text"
              required
              disabled={disableForm}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextFieldInput
              fullWidth
              label={t('admin.usersPage.userDetails.email')}
              name="email.address"
              type="text"
              required
              disabled={disableForm || disableEmailField}
            />
          </Grid>
          {canHasAccountManager && (
            <Grid size={{ xs: 6 }} container direction="row" alignItems="center">
              <Checkbox
                name="accountManager"
                label={t('admin.usersPage.userDetails.accountManager')}
                checked={isAccountManager}
                onChange={toggleUserType}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button data-testid="click-cancel" size="medium" color="primary" onClick={onCancel}>
          {t('admin.usersPage.rolesView.buttonCancel')}
        </Button>
        <Button size="medium" color="primary" type="submit" variant="contained">
          {disableForm ? t('admin.usersPage.rolesView.buttonSave') : t('admin.adminUsersPage.userForm.saveButtonTitle')}
        </Button>
      </Box>
    </Form>
  )
}
